import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  Signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { InputModule } from '@epicuro-next/ataraxia/input';
import { dynamicElementsUserFeedbackKey } from '@epicuro-next/constants/dynamic-elements';
import { DontTreeShake } from '@epicuro-next/elements/core';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import { slideAnimation } from '@epicuro-next/ui';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { AtaraxiaTypeWriterComponent } from 'ataraxia-type-writer';
import { UserFeedbackService } from 'data-user-feedback';
import {
  IUserFeedbackAnimationType,
  IUserFeedbackAnswer,
  IUserFeedbackAnswerType,
  IUserFeedbackQuestion,
  IUserFeedbackQuestionContextType,
} from 'models-user-feedback';

import { UserFeedbackStore } from './user-feedback.store';

const commentYesLabel = $localize`:UserFeedback|Feedback yes comment label@@commentYesFeedbackLabel:Tell us what we can do better`;
const commentNoLabel = $localize`:UserFeedback|Feedback no comment label@@commentNoFeedbackLabel:Tell us what we can do better`;

@DynamicComponentRegister(dynamicElementsUserFeedbackKey)
@Component({
  selector: 'epc-user-feedback',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FaIconComponent,
    InputModule,
    AtaraxiaTypeWriterComponent,
  ],
  providers: [UserFeedbackStore, UserFeedbackService],
  templateUrl: './user-feedback.component.html',
  styleUrl: './user-feedback.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [slideAnimation],
})
export class UserFeedbackComponent implements OnInit, OnDestroy {
  private readonly componentStore = inject(UserFeedbackStore);
  public contextType = input<IUserFeedbackQuestionContextType>(
    IUserFeedbackQuestionContextType.OrderPlaced,
  );
  public writeEffectEnabled = input(true);
  public animationType = input<IUserFeedbackAnimationType>(
    IUserFeedbackAnimationType.SlideFromRight,
  );
  public writeDelay = input<number>(300);

  public readonly submitting: Signal<boolean> = this.componentStore.submitting;
  public readonly submitted: Signal<boolean> = this.componentStore.submitted;
  public readonly question: Signal<IUserFeedbackQuestion | null> =
    this.componentStore.question;
  public readonly commentControl = new FormControl<string>('');
  public readonly answerType = signal<IUserFeedbackAnswerType | null>(null);
  public readonly answerSelected = computed(() => this.answerType() !== null);

  public readonly questionClosed = signal(false);
  public readonly questionTyped = signal(false);
  public readonly commentLabelTyped = signal(false);
  public readonly commentLabel = computed(() =>
    this.answerType() === IUserFeedbackAnswerType.Yes
      ? this.question()?.yesText || commentYesLabel
      : this.question()?.noText || commentNoLabel,
  );

  public ngOnInit() {
    this.componentStore.loadQuestion(this.contextType());
  }

  public ngOnDestroy() {
    if (!this.submitted()) {
      this.submitFeedback();
    }
  }

  public answerQuestion(result: boolean) {
    this.answerType.set(
      result ? IUserFeedbackAnswerType.Yes : IUserFeedbackAnswerType.No,
    );
  }

  public submitFeedback() {
    if (this.question() && this.answerSelected()) {
      const answer: IUserFeedbackAnswer = {
        questionId: this.question()?.id as number,
        type: this.answerType() as IUserFeedbackAnswerType,
        comment: this.commentControl.value?.trim() ?? '',
      };
      this.componentStore.submitAnswer(answer);
    }
  }
}

DontTreeShake(UserFeedbackComponent);
